<template>
	<q-layout view="hHh lpr fFf">
		<div id="app" v-scroll="scrolled">
			<template v-if="user">
				<q-header elevated :reveal="headerReveal" class="bg-white text-grey-8 q-py-xs">
					<navigation/>
					<q-separator/>
					<searchbar/>
				</q-header>

				<q-page-container class="page-container">
					<router-view class="q-pa-md"/>
				</q-page-container>

				<q-footer bordered class="page-footer">
					<active-watchlist-selector/>

					<virtual-log-builder/>
				</q-footer>

				<dialog-shell/>
			</template>
			<template v-else-if="publicRoute">
				<q-header elevated class="bg-white text-grey-8 q-py-xs">
					<navigation/>
				</q-header>

				<q-page-container class="page-container">
					<router-view class="q-pa-md"/>
				</q-page-container>

				<q-footer>

				</q-footer>

				<dialog-shell/>
			</template>
		</div>
	</q-layout>
</template>

<script>
	import Searchbar from "@/components/main/Searchbar";
	import DialogShell from "@/components/dialog/DialogShell";
	import Navigation from "@/components/main/Navigation";
	import ActiveWatchlistSelector from "@/components/main/ActiveWatchlistSelector";
	import {FETCH_COMPANY_USERS, FETCH_SPECIES_FILTERS, SET_SNACK, USER_AUTH} from "@/store/actions.type";
	import {SET_DISPLAY_ADVANCED_SEARCH} from "@/store/mutations.type";
	import {mapGetters} from "vuex";
	import {
		advancedSearchAutoHideWindowWidth,
		footerSelector,
		loginContainerSelector,
		snackLevelColors
	} from "@/common/config";
	import VirtualLogBuilder from "@/components/common/VirtualLogBuilder";
	import {SNACK_LEVEL} from "@/common/constants";
	import NavigationMixin from "@/mixins/NavigationMixin";
	import ZohoAuthentication from "@/components/zoho/ZohoAuthentication";
	import throttle from "quasar/src/utils/throttle";

	export default {
		name: "App",
		components: {ZohoAuthentication, VirtualLogBuilder, ActiveWatchlistSelector, Navigation, DialogShell, Searchbar},
		mixins: [NavigationMixin],
		async mounted() {
			await this.$store.dispatch(USER_AUTH).then(() => {
				const loginContainer = document.querySelector(loginContainerSelector),
					footerContainer = document.querySelector(footerSelector);

				if (this.user) {
					this.$store.dispatch(FETCH_SPECIES_FILTERS);
					this.$store.dispatch(FETCH_COMPANY_USERS);
				} else if (!this.publicRoute) {
					// reveal login panel if the user is not logged in and is not on a public route
					loginContainer.style.setProperty('display', '');
				}
				if (loginContainer && this.publicRoute) {
					loginContainer.remove();
				}

				//footerContainer.style.setProperty('display', '');
			});
		},
		computed: {
			...mapGetters(['user', 'snack', 'snackLevel', 'headerReveal']),
			publicRoute() {
				return (this.$route.meta.hasOwnProperty('requiresAuth') && !this.$route.meta.requiresAuth);
			}
		},
		watch: {
			user() {
				// check for global initial route config for deeplinks
				if (typeof route !== "undefined") {
					window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
					this.$router.push(route);
				}
			},
			snack() {
				if (this.snack) {
					this.$q.notify({
						color: snackLevelColors[this.snackLevel],
						message: this.$t(`snack.${this.snack}`)
					});
					this.$store.dispatch(SET_SNACK, {snack: '', snackLevel: SNACK_LEVEL.SUCCESS});
				}
			}
		},
		methods: {
			scrolled: throttle(function (position) {
				if (window.innerWidth > advancedSearchAutoHideWindowWidth) {
					this.$store.commit(SET_DISPLAY_ADVANCED_SEARCH, false);
				}
			}, 1000),
		}
	}
</script>

<style lang="scss">
	:root {
		--color-background: #f4f4f4;
		--image-placeholder-color: #cccccc;

		--font-size: 1rem;
		--font-size-sd: 0.75rem;
		--font-size-md: 1.25rem;
		--font-size-lg: 2rem;
	}

  body[data-neutral="1"] {
    --q-color-primary: #333 !important;
  }

	#loginContainer {
		width: 100%;
		height: 100vh;

		.tx-felogin-pi1 {
			display: contents;
		}

		.loginForm {
			min-width: 320px;
			min-height: 320px;
			max-width: 400px;
			width: 100%;
			background: rgba(255, 255, 255, 0.8);

			&.forgotForm {
				height: auto;
				max-width: 550px;
			}
		}

		.loginAdditionalLink {
			margin: 8px 0 0;
		}

		& ~ .q-layout {
			display: none;
		}
	}

	body {
		background: var(--color-background);

		a {
			text-decoration: none;
			color: var(--q-color-primary);
			cursor: pointer;
		}

		h1, .text-h1 {
			font-size: 3rem;
			line-height: 1;
		}

		h2, .text-h2 {
			font-size: 2rem;
			line-height: 1;
		}

		h3, .text-h3 {
			font-size: 1.75rem;
			line-height: 1;
		}

		h4, .text-h4 {
			font-size: 1.25rem;
			line-height: 1;
			margin: 0 0 1rem 0;
		}

		h5, .text-h5 {
			font-size: 1.25rem;
			color: var(--main-color);
			line-height: 1;
			margin: 0.5rem 0;
		}

		.subheading {
			color: var(--q-color-secondary);
			font-size: var(--font-size-sd);
			margin: 0 0 1rem 0;
			position: relative;
			top: -0.5rem;
			left: 0.05rem;
		}

		.text-overline {
			color: rgba(0, 0, 0, 0.6);
			font-weight: 400;
			letter-spacing: 0.00937em;
			font-size: 14px;
			line-height: 20px;
		}

		@media(min-width: 600px) {
			h1, .text-h1 {
				font-size: 4rem;
			}

			h2, .text-h2 {
				font-size: 3rem;
			}

			h3, .text-h3 {
				font-size: 2.5rem;
			}

			h4, .text-h4 {
				font-size: 2rem;
			}

			h5, .text-h5 {
				font-size: 1.5rem;
				margin: 1rem 0;
			}

			.subheading {
				top: -1rem;
			}
		}
	}

	.page-container {
		max-width: 1590px;
		margin: 0 auto;
	}

	#app .page-footer {
		background-color: #fff;
		border-color: #e9e9e9;
	}

	.sticky-header-table {
		max-height: 600px;
		max-width: 100%;

		.q-table__top,
		.q-table__bottom,
		thead tr:first-child th {
			background-color: #fff;
      z-index: 3;
		}

		thead tr th {
			position: sticky;
			z-index: 1;
		}

		thead tr:first-child th {
			top: 0;
		}

		&.q-table--loading {
			min-height: 200px;
		}
	}

  .first-column-sticky {
    th:first-child, td:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background-color: #fff;
    }
  }

	.large-dialog {
		width: 600px;
		max-width: 100%;
	}

	.large-cta-btn {
		width: 220px;
		max-width: 100%;
	}

	.teaser-selector {
		color: var(--q-color-accent-text);
		opacity: 0.6;
		transition: opacity .5s ease;

		&.selected {
			color: var(--q-color-primary);
			opacity: 0.8;
		}
	}

	#warehouseFooter {
		padding: 0.5rem 0 2.725rem;
		background: #d4d4d4;

		#warehouseFooterInner {
			max-width: 1590px;
			margin: 0 auto;
			text-align: right;

			> div {
				padding-top: 0;
			}
		}

		a {
			color: rgba(10,10,10,.5);

			&:not(:last-child):after {
				content: "|";
				margin: 0 .625rem;
				color: rgba(30,30,30,.5);
			}
		}
	}
</style>